import React, { useState } from 'react';
import { X, Calendar, Upload, Link, AlertCircle, HelpCircle, GraduationCap } from 'lucide-react';
import { CalendarEvent, parseICalendar, fetchCalendarFromURL, readCalendarFile, groupEventsByCourse } from '@/lib/icalParser';
import CalendarHelpModal from './CalendarHelpModal';

interface CalendarImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onEventsImported: (events: CalendarEvent[]) => void;
  mode: 'student' | 'personal'; // Student has Canvas, Personal doesn't
}

type ImportMethod = 'url' | 'file';
type CalendarSource = 'canvas' | 'google' | 'apple' | 'outlook';

const CalendarImportModal: React.FC<CalendarImportModalProps> = ({ 
  isOpen, 
  onClose, 
  onEventsImported,
  mode 
}) => {
  const [importMethod, setImportMethod] = useState<ImportMethod>('url');
  const [calendarURL, setCalendarURL] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedSource, setSelectedSource] = useState<CalendarSource>(
    mode === 'student' ? 'canvas' : 'google'
  );
  const [showHelp, setShowHelp] = useState(false);

  if (!isOpen) return null;

  const handleImport = async () => {
    setError('');
    setLoading(true);

    try {
      let icalText = '';

      if (importMethod === 'url') {
        if (!calendarURL.trim()) {
          throw new Error('Please enter a calendar URL');
        }
        icalText = await fetchCalendarFromURL(calendarURL);
      } else {
        if (!selectedFile) {
          throw new Error('Please select a file');
        }
        icalText = await readCalendarFile(selectedFile);
      }

const events = parseICalendar(icalText, selectedSource);

      if (events.length === 0) {
        throw new Error('No events found in calendar');
      }

      // Show course code summary before passing events
      const courseGroups = groupEventsByCourse(events);
      const courseSummary = Array.from(courseGroups.entries())
        .filter(([code]) => code !== 'Uncategorized')
        .map(([code, evts]) => `${code} (${evts.length} events)`)
        .join(', ');
      if (courseSummary) {
        console.log('📚 Detected courses:', courseSummary);
      }

      onEventsImported(events);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to import calendar');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setError('');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="bg-gradient-to-br from-gray-900 via-purple-900/50 to-gray-900 rounded-2xl border-2 border-amber-400/30 shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-600 to-yellow-500 p-4 flex items-center justify-between border-b-2 border-amber-700">
          <div className="flex items-center gap-3">
            <Calendar className="w-6 h-6 text-gray-900" />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Import Calendar</h2>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowHelp(true)}
              className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors flex items-center gap-2"
              title="Help with importing calendars"
            >
              <HelpCircle className="w-5 h-5 text-gray-900" />
              <span className="text-sm font-semibold text-gray-900">Help</span>
            </button>
            <button
              onClick={onClose}
              className="p-1 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-6 h-6 text-gray-900" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Calendar Source Selection */}
          <div>
            <label className="block text-amber-300 font-semibold mb-3">
              Calendar Source
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {mode === 'student' && (
                <button
                  onClick={() => setSelectedSource('canvas')}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    selectedSource === 'canvas'
                      ? 'border-amber-400 bg-amber-400/20'
                      : 'border-gray-600 hover:border-amber-400/50'
                  }`}
                >
                  <div className="text-center">
                    <div className="text-2xl mb-1">🎓</div>
                    <div className="text-sm font-semibold text-white">Canvas</div>
                  </div>
                </button>
              )}
              <button
                onClick={() => setSelectedSource('google')}
                className={`p-4 rounded-xl border-2 transition-all ${
                  selectedSource === 'google'
                    ? 'border-amber-400 bg-amber-400/20'
                    : 'border-gray-600 hover:border-amber-400/50'
                }`}
              >
                <div className="text-center">
                  <div className="text-2xl mb-1">📅</div>
                  <div className="text-sm font-semibold text-white">Google</div>
                </div>
              </button>
              <button
                onClick={() => setSelectedSource('apple')}
                className={`p-4 rounded-xl border-2 transition-all ${
                  selectedSource === 'apple'
                    ? 'border-amber-400 bg-amber-400/20'
                    : 'border-gray-600 hover:border-amber-400/50'
                }`}
              >
                <div className="text-center">
                  <div className="text-2xl mb-1">🍎</div>
                  <div className="text-sm font-semibold text-white">Apple</div>
                </div>
              </button>
              <button
                onClick={() => setSelectedSource('outlook')}
                className={`p-4 rounded-xl border-2 transition-all ${
                  selectedSource === 'outlook'
                    ? 'border-amber-400 bg-amber-400/20'
                    : 'border-gray-600 hover:border-amber-400/50'
                }`}
              >
                <div className="text-center">
                  <div className="text-2xl mb-1">📧</div>
                  <div className="text-sm font-semibold text-white">Outlook</div>
                </div>
              </button>
            </div>
          </div>

          {/* Import Method Tabs */}
          <div>
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setImportMethod('url')}
                className={`flex-1 py-2 px-4 rounded-lg font-semibold transition-all ${
                  importMethod === 'url'
                    ? 'bg-amber-500 text-gray-900'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                <Link className="w-4 h-4 inline mr-2" />
                Calendar URL
              </button>
              <button
                onClick={() => setImportMethod('file')}
                className={`flex-1 py-2 px-4 rounded-lg font-semibold transition-all ${
                  importMethod === 'file'
                    ? 'bg-amber-500 text-gray-900'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                <Upload className="w-4 h-4 inline mr-2" />
                Upload File
              </button>
            </div>

            {/* URL Input */}
            {importMethod === 'url' && (
              <div>
                <label className="block text-amber-300 font-semibold mb-2">
                  Calendar Feed URL
                </label>
                <input
                  type="url"
                  value={calendarURL}
                  onChange={(e) => setCalendarURL(e.target.value)}
                  placeholder={
                    selectedSource === 'canvas'
                      ? 'https://canvas.school.edu/feeds/calendars/...'
                      : 'https://calendar.google.com/calendar/ical/...'
                  }
                  className="w-full bg-gray-800 border-2 border-gray-600 rounded-lg px-4 py-3 text-white focus:border-amber-400 focus:outline-none"
                />
                <p className="text-xs text-gray-400 mt-2">
                  {selectedSource === 'canvas' && 'Go to Canvas → Calendar → Calendar Feed to get your URL'}
                  {selectedSource === 'google' && 'Go to Google Calendar → Settings → Your calendar → Secret address in iCal format'}
                  {selectedSource === 'apple' && 'On Mac: Calendar → File → Export → Save as .ics file, then upload below'}
                  {selectedSource === 'outlook' && 'Go to Outlook Calendar → Settings → Shared calendars → Publish → ICS'}
                </p>
              </div>
            )}

            {/* File Upload */}
            {importMethod === 'file' && (
              <div>
                <label className="block text-amber-300 font-semibold mb-2">
                  Upload .ics File
                </label>
                <input
                  type="file"
                  accept=".ics,.ical"
                  onChange={handleFileChange}
                  className="hidden"
                  id="calendar-file"
                />
                <label
                  htmlFor="calendar-file"
                  className="block w-full bg-gray-800 border-2 border-dashed border-gray-600 rounded-lg px-4 py-8 text-center cursor-pointer hover:border-amber-400 transition-colors"
                >
                  <Upload className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                  {selectedFile ? (
                    <p className="text-white font-semibold">{selectedFile.name}</p>
                  ) : (
                    <>
                      <p className="text-white font-semibold mb-1">Click to upload .ics file</p>
                      <p className="text-xs text-gray-400">Or drag and drop</p>
                    </>
                  )}
                </label>
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-500/20 border-2 border-red-500 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-red-200 text-sm">{error}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleImport}
              disabled={loading}
              className="flex-1 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-gray-900 font-bold py-3 px-6 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Importing...' : 'Import Events'}
            </button>
          </div>
        </div>
      </div>

      {/* Help Modal */}
      <CalendarHelpModal
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
        calendarSource={selectedSource}
      />
    </div>
  );
};

export default CalendarImportModal;
